import SoftBox from "../../../components/SoftBox";
import {useCallback, useEffect, useState} from "react";
import childApiClient from "../../../api/children/childrenApiClient";
import Grid from "@mui/material/Grid";
import {useParams} from "react-router-dom";
import SoftAlert from "../../../components/Alert";
import Card from "@mui/material/Card";
import SoftTypography from "../../../components/SoftTypography";
import SoftAvatar from "../../../components/Avatar";
import {v4 as uuidv4} from "uuid";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SoftButton from "../../../components/SoftButton";
import DeleteConfirmDialog from "../../../components/Dialogs/DeleteConfirmDialog";
import GuardianRelationshipManager from "./GuardianRelationshipManager";

function ChildDetail() {
    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [child, setChild] = useState(null);
    const [guardians, setGuardians] = useState([]);
    const [childImages, setChildImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [guardianManagerOpen, setGuardianManagerOpen] = useState(false);
    const {childId} = useParams();

    const loadChildDetails = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('Loading child details for ID:', childId);

            // Debug authentication
            const token = localStorage.getItem('token');
            console.log('Authentication token present:', !!token);
            if (token) {
                console.log('Token length:', token.length);
                console.log('Token starts with:', token.substring(0, 20) + '...');
            }

            if (!childId) {
                throw new Error('Child ID is required');
            }

            // Load child details with guardians and images
            const childDetailResponse = await childApiClient.getChildDetail(childId);
            console.log('Child detail response:', childDetailResponse);

            // Handle different response structures
            let childDetailData = null;
            if (childDetailResponse && childDetailResponse.data) {
                childDetailData = childDetailResponse.data;
            } else if (childDetailResponse) {
                childDetailData = childDetailResponse;
            } else {
                throw new Error('No child detail data received');
            }

            console.log('Child detail data to set:', childDetailData);
            console.log('Guardian relationships in response:', childDetailData.guardianRelationships);
            if (childDetailData.guardianRelationships && childDetailData.guardianRelationships.length > 0) {
                console.log('First guardian relationship:', childDetailData.guardianRelationships[0]);
                console.log('First guardian has profilePic:', childDetailData.guardianRelationships[0].profilePic ? 'Yes' : 'No');
                console.log('Guardian relationship data structure:', Object.keys(childDetailData.guardianRelationships[0]));
            }
            console.log('Child data structure:', childDetailData.child);
            console.log('Child guardianRelationships field:', childDetailData.child?.guardianRelationships);

            // Extract child information
            if (childDetailData.child) {
                console.log('Setting child data:', childDetailData.child);
                setChild(childDetailData.child);
            }

            // Extract guardian information
            if (childDetailData.guardianRelationships && Array.isArray(childDetailData.guardianRelationships)) {
                console.log('Setting guardian relationships:', childDetailData.guardianRelationships);
                setGuardians(childDetailData.guardianRelationships);
            } else {
                console.log('No guardian relationships found, setting empty array');
                setGuardians([]);
            }

            // Extract image information
            if (childDetailData.child && childDetailData.child.images && Array.isArray(childDetailData.child.images)) {
                console.log('Setting child images:', childDetailData.child.images);
                console.log('First image structure:', childDetailData.child.images[0]);
                setChildImages(childDetailData.child.images);
            } else {
                console.log('No images found in child data, trying separate images endpoint');
                console.log('Child data structure:', childDetailData.child);

                // Fallback: try to load images separately
                try {
                    const imagesResponse = await childApiClient.getChildImages(childId);
                    console.log('Separate images response:', imagesResponse);

                    let imagesData = [];
                    if (Array.isArray(imagesResponse)) {
                        imagesData = imagesResponse;
                    } else if (imagesResponse && Array.isArray(imagesResponse.data)) {
                        imagesData = imagesResponse.data;
                    }

                    console.log('Setting images from separate endpoint:', imagesData);
                    setChildImages(imagesData);
                } catch (imagesError) {
                    console.error('Error loading images separately:', imagesError);
                    setChildImages([]);
                }
            }

        } catch (error) {
            console.error('Error loading child details:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            });

            // Handle specific error cases
            if (error.response?.status === 403) {
                console.warn('Access forbidden for child endpoint - may need authentication or proper permissions');
                setError('Access denied. You may not have permission to view child details or need to log in.');
            } else if (error.response?.status === 404) {
                setError('Child not found. The requested child may not exist or has been removed.');
            } else {
                setError(`Failed to load child details: ${error.message}`);
            }

            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    }, [childId]);

    useEffect(() => {
        if (childId) {
            loadChildDetails();
        }
    }, [childId, loadChildDetails]);

    const handleDeleteImage = (image) => {
        setImageToDelete(image);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDeleteImage = async () => {
        try {
            await childApiClient.deleteChildImage(childId, imageToDelete.imageId);
            await loadChildDetails(); // Reload full child detail after deletion
            setDeleteConfirmOpen(false);
            setImageToDelete(null);
        } catch (error) {
            console.error('Error deleting image:', error);
            setError('Failed to delete image. Please try again.');
            setShowAlert(true);
        }
    };

    const handleCancelDeleteImage = () => {
        setDeleteConfirmOpen(false);
        setImageToDelete(null);
    };

    const handleImageUpload = async (event) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            try {
                const formData = new FormData();
                for (let i = 0; i < files.length; i++) {
                    formData.append('images', files[i]);
                }

                await childApiClient.uploadChildImages(childId, formData);
                await loadChildDetails(); // Reload full child detail after upload
            } catch (error) {
                console.error('Error uploading images:', error);
                setError('Failed to upload images. Please try again.');
                setShowAlert(true);
            }
        }
    };

    if (loading) {
        return (
            <SoftBox p={2} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <SoftBox>Loading child details...</SoftBox>
            </SoftBox>
        );
    }

    if (!child) {
        return (
            <SoftBox p={2}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card sx={{height: "100%"}}>
                            <SoftBox p={3} textAlign="center">
                                <SoftTypography variant="h6" fontWeight="medium" color="text.secondary" mb={2}>
                                    Child Details
                                </SoftTypography>
                                <SoftTypography variant="body2" color="text.secondary" mb={2}>
                                    Child ID: {childId}
                                </SoftTypography>
                                <SoftTypography variant="body2" color="text.secondary">
                                    Unable to load child details. This may be due to:
                                </SoftTypography>
                                <SoftBox component="ul" textAlign="left" mt={2} pl={3}>
                                    <SoftBox component="li" mb={1}>
                                        <SoftTypography variant="body2" color="text.secondary">
                                            Insufficient permissions to view this child
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox component="li" mb={1}>
                                        <SoftTypography variant="body2" color="text.secondary">
                                            The child may not exist or has been removed
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox component="li" mb={1}>
                                        <SoftTypography variant="body2" color="text.secondary">
                                            Authentication issues - please try logging in again
                                        </SoftTypography>
                                    </SoftBox>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Grid>
                </Grid>

                {error && (
                    <SoftAlert
                        color="error"
                        dismissible={false}
                        timeout={10}
                        mount={showAlert}
                        onClose={() => {
                            setError(null);
                            setShowAlert(false);
                        }}
                    >
                        {error}
                    </SoftAlert>
                )}
            </SoftBox>
        );
    }

    const renderChildInfo = () => {
        const labels = [];
        const values = [];
        Object.keys(child).forEach((el) => {
            const notShow = ["profilePic", "id", "childId", "createdAt", "updatedAt", "images", "active"];
            if (el.match(/[A-Z\s]+/) && !notShow.includes(el)) {
                const uppercaseLetter = Array.from(el).find((i) => i.match(/[A-Z]+/));
                const newElement = el.replace(uppercaseLetter, ` ${uppercaseLetter.toLowerCase()}`);
                labels.push(newElement);
                values.push(child[el]);
            } else if (!notShow.includes(el)) {
                labels.push(el);
                values.push(child[el]);
            }
        });

        return labels.map((label, key) => (
            <SoftBox key={label} display="flex" py={1} pr={2}>
                <SoftTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    {label}: &nbsp;
                </SoftTypography>
                <SoftTypography variant="button" fontWeight="regular" color="text">
                    &nbsp;{values[key]}
                </SoftTypography>
            </SoftBox>
        ));
    };

    const renderImageGallery = () => (
        <Card sx={{height: "100%"}}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
                <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                    Image Gallery
                </SoftTypography>
                <SoftBox>
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{display: 'none'}}
                        id="image-upload"
                    />
                    <label htmlFor="image-upload">
                        <SoftButton
                            component="span"
                            color="primary"
                            size="small"
                            startIcon={<AddIcon/>}
                        >
                            Add Images
                        </SoftButton>
                    </label>
                </SoftBox>
            </SoftBox>
            <SoftBox p={2}>
                {loading ? (
                    <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                        <SoftTypography variant="body2" color="text.secondary">
                            Loading images...
                        </SoftTypography>
                    </SoftBox>
                ) : childImages.length > 0 ? (
                    <Grid container spacing={2}>
                        {childImages.map((image, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={image.imageId || index}>
                                <Card sx={{
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                    },
                                }}>
                                    <img
                                        src={image.publicImageUrl || image.imageUrl || (image.imagePath ? `/api/images?path=${encodeURIComponent(image.imagePath)}` : '/placeholder-image.jpg')}
                                        alt={`Child ${index + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                            console.error('Failed to load image:', image);
                                            console.error('Image details:', {
                                                imageId: image.imageId,
                                                publicImageUrl: image.publicImageUrl,
                                                imageUrl: image.imageUrl,
                                                imagePath: image.imagePath
                                            });
                                            // Try fallback to imagePath API endpoint if URLs failed
                                            if (image.imagePath && !e.target.src.includes('/api/images')) {
                                                e.target.src = `/api/images?path=${encodeURIComponent(image.imagePath)}`;
                                            } else {
                                                e.target.style.display = 'none';
                                            }
                                        }}
                                    />
                                    <IconButton
                                        color="error"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                            },
                                        }}
                                        onClick={() => handleDeleteImage(image)}
                                    >
                                        <DeleteIcon/>
                                    </IconButton>
                                    {image.uploadedAt && (
                                        <SoftBox
                                            sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                left: 8,
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {new Date(image.uploadedAt).toLocaleDateString()}
                                        </SoftBox>
                                    )}
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                        <SoftTypography variant="body2" color="text.secondary">
                            No images available
                        </SoftTypography>
                    </SoftBox>
                )}
            </SoftBox>
        </Card>
    );

    return (
        <SoftBox p={2}>
            <Grid container spacing={3}>
                {/* Child Information */}
                <Grid item xs={12} md={6} xl={6}>
                    <Card sx={{height: "100%"}}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <SoftBox pt={2} px={2}>
                                    <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                                        {child.firstName} {child.lastName}
                                    </SoftTypography>
                                    <SoftBox
                                        key={uuidv4()}
                                        component="td"
                                        p={1}
                                        textAlign="center"
                                    >
                                        <img
                                            src={child.profilePicUrl || `data:image/png;base64,${child.profilePic}`}
                                            alt="profile-image"
                                            style={{width: "100%", height: "100%"}}
                                            onError={(e) => {
                                                console.error('Failed to load profile picture:', child);
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={6}>
                                <SoftBox p={2}>
                                    <SoftBox>
                                        {renderChildInfo()}
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                {/* Guardians Information */}
                <Grid item xs={12} md={6} xl={6}>
                    <Card sx={{height: "100%"}}>
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
                            <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                                Guardians
                            </SoftTypography>
                            <SoftButton
                                variant="outlined"
                                color="text"
                                size="small"
                                onClick={() => setGuardianManagerOpen(true)}
                            >
                                Manage Guardians
                            </SoftButton>
                        </SoftBox>
                        <SoftBox p={2}>
                            {loading ? (
                                <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                    <SoftTypography variant="body2" color="text.secondary">
                                        Loading guardians...
                                    </SoftTypography>
                                </SoftBox>
                            ) : guardians.length > 0 ? (
                                <SoftBox component="ul" display="flex" flexDirection="column" p={0} m={0}>
                                    {guardians.map((guardian, index) => (
                                        <SoftBox key={index} component="li" display="flex"
                                                 justifyContent="space-between" alignItems="center" p={1} px={2}>
                                            <SoftBox mr={2} display="flex">
                                                <SoftBox mr={2}>
                                                    <SoftAvatar
                                                        src={guardian?.profilePicUrl || (guardian?.profilePic ? `data:image/png;base64,${guardian.profilePic}` : undefined)}
                                                        alt="guardian profile"
                                                        variant="rounded"
                                                        shadow="md"
                                                    />
                                                </SoftBox>
                                                <SoftBox mr={2} display="flex" flexDirection="column">
                                                    <SoftTypography variant="button" fontWeight="medium">
                                                        Name: {guardian?.guardianFirstName} {guardian?.guardianLastName}
                                                    </SoftTypography>
                                                    <SoftTypography variant="caption" color="text">
                                                        Email: {guardian?.guardianEmail}
                                                    </SoftTypography>
                                                    <SoftTypography variant="caption" color="text">
                                                        Relationship: {guardian?.relationship}
                                                    </SoftTypography>
                                                </SoftBox>
                                            </SoftBox>
                                        </SoftBox>
                                    ))}
                                </SoftBox>
                            ) : (
                                <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                    <SoftTypography variant="body2" color="text.secondary">
                                        No guardians found for this child
                                    </SoftTypography>
                                </SoftBox>
                            )}
                        </SoftBox>
                    </Card>
                </Grid>

                {/* Image Gallery */}
                <Grid item xs={12}>
                    {renderImageGallery()}
                </Grid>
            </Grid>

            {error && (
                <SoftAlert
                    color="error"
                    dismissible={false}
                    timeout={10}
                    mount={showAlert}
                    onClose={() => {
                        setError(null);
                        setShowAlert(false);
                    }}
                >
                    {error}
                </SoftAlert>
            )}
            <DeleteConfirmDialog
                open={deleteConfirmOpen}
                onClose={handleCancelDeleteImage}
                onDelete={handleConfirmDeleteImage}
                warningMessage={`Are you sure you want to delete this image? This action cannot be undone.`}
            />

            <GuardianRelationshipManager
                open={guardianManagerOpen}
                onClose={() => setGuardianManagerOpen(false)}
                childId={parseInt(childId)}
                guardianRelationships={guardians || []}
                onUpdate={loadChildDetails}
            />
        </SoftBox>
    );
}

export default ChildDetail;
